import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bell } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { NOTIFICATIONS_KEY, useNotifications } from "../../features/cards/card-queries";
import { formatDateTime } from "../../features/search/format";
import { useApi } from "../api-context";

/** The bell: approval requests for the admin, decisions for the employee (D-64). Opening it marks all read. */
export function NotificationBell() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const list = useNotifications();
  const unread = list.data?.unread ?? 0;
  const items = list.data?.data ?? [];

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open && unread > 0)
          void api
            .markNotificationsRead()
            .then(() => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }));
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          aria-label={unread > 0 ? `Notifications (${String(unread)} new)` : "Notifications"}
        >
          <Bell aria-hidden="true" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -end-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Nothing yet.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.map((n) => (
              <DropdownMenuItem
                key={n.id}
                className="flex flex-col items-start gap-0.5"
                onSelect={() => {
                  if (n.link !== null) void navigate({ to: n.link });
                }}
              >
                <span className={n.readAt === null ? "font-semibold" : "font-medium"}>{n.title}</span>
                <span className="line-clamp-2 text-xs text-muted-foreground">{n.body}</span>
                <span className="text-[11px] text-muted-foreground">{formatDateTime(n.createdAt)}</span>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
